/**
 * Phase 13 / Task 13.2b (migration 0235) — "Enter reading" on a portal tank card.
 *
 * An admin, foreman or pumper records the level they gauged: BBL and when (now by default, local
 * time). It is stored on the company's own tank, so the haulers of that tank use it for their
 * forecasts too (Corbin, 2026-10-19). The panel lists the tank's recent readings, the company's
 * and the hauler's; the company's own can be voided (they then count for nothing). A tank not set
 * up yet (Tank setup) cannot take a reading, and the panel says so.
 *
 * One client key per opening of the panel, so a double click or a retry stores one reading.
 *
 * Tank registry PR 1 (0245): the level can be entered in BBL or in inches (a unit toggle). The
 * API turns inches into barrels with the tank's bbl per inch; when the tank has none yet, the
 * reading is kept with no barrels and the list shows it raw: "8 ft 4 in, size unknown" (13.8, D-23c).
 */

import React from 'react';
import { ApiError } from '@lodex-pro/web-shared';
import { formatTankReading } from '@lodex-pro/shared/tank-level-format';
import {
  useAddPortalTankReading,
  usePortalTankReadings,
  useVoidPortalTankReading,
  type PortalTankReadingRow,
} from '../hooks/useTankReadings';

export interface TankReadingPanelTank {
  readonly tankId: string;
  readonly tankName: string;
  readonly capacityBbl: string | null;
}

function toLocalInput(d: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${String(d.getFullYear())}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function newClientKey(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `ptr-${String(Date.now())}-${Math.random().toString(36).slice(2, 12)}`;
}

/** Plain words for the API's refusals. */
export function portalReadingErrorMessage(err: unknown, tank: TankReadingPanelTank): string {
  if (err instanceof ApiError) {
    const reason = err.fields[0]?.reason;
    if (reason === 'in_the_future') return 'That time is in the future.';
    if (reason === 'too_old') return 'That time is more than a year ago.';
    if (reason === 'over_capacity') return `That is more than ${tank.tankName} holds.`;
    if (reason === 'tank_not_set_up') {
      return `${tank.tankName} is not set up yet. Set it up under Tank setup, then enter readings.`;
    }
    if (err.status === 404) return `${tank.tankName} is not one of your tanks.`;
    if (err.status === 403) return 'You cannot enter readings here.';
  }
  return 'The reading was not saved. Try again.';
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '40px',
  padding: '8px 10px',
  fontSize: '14px',
  background: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  color: 'var(--color-text-primary)',
};

const buttonStyle: React.CSSProperties = {
  minHeight: '36px',
  padding: '0 12px',
  borderRadius: 'var(--radius-pill)',
  border: '1px solid var(--color-border-secondary)',
  background: 'var(--color-bg-tertiary)',
  color: 'var(--color-text-primary)',
  cursor: 'pointer',
};

export type ReadingUnit = 'BBL' | 'IN';

/**
 * A reading row for display (13.8, D-23c): the shared formatter, as stored. "120 BBL",
 * "120 BBL (4 ft 0 in)", or, with no barrels yet, "4 ft 0 in, size unknown". Never an estimate.
 */
export function portalReadingLevelText(r: {
  readonly levelBbl: number | null;
  /** Older rows may omit it. */
  readonly levelInches?: number | null;
}): string {
  const levelIn = r.levelInches ?? null;
  return formatTankReading({
    levelIn,
    volumeBbl: r.levelBbl,
    needsSize: r.levelBbl === null && levelIn !== null,
  });
}

function ReadingItem({
  tankId,
  reading,
}: {
  tankId: string;
  reading: PortalTankReadingRow;
}): React.JSX.Element {
  const voidIt = useVoidPortalTankReading(tankId, reading.id);
  const voided = reading.voidedAt !== null;
  return (
    <li
      data-testid="portal-tank-reading"
      style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', padding: '4px 0' }}
    >
      <span style={voided ? { textDecoration: 'line-through', opacity: 0.6 } : undefined}>
        {portalReadingLevelText(reading)} · {new Date(reading.readAt).toLocaleString()} ·{' '}
        {reading.own ? 'your company' : 'the hauler'}
      </span>
      {reading.own && !voided && (
        <button
          type="button"
          style={{ ...buttonStyle, minHeight: '28px' }}
          disabled={voidIt.isPending}
          onClick={(): void => {
            if (!window.confirm('Void this reading? It will no longer count for this tank.')) {
              return;
            }
            voidIt.mutate({});
          }}
        >
          Void
        </button>
      )}
      {voided && <span style={{ fontSize: '12px' }}>voided</span>}
    </li>
  );
}

export function TankReadingPanel({ tank }: { tank: TankReadingPanelTank }): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const [level, setLevel] = React.useState('');
  const [unit, setUnit] = React.useState<ReadingUnit>('BBL');
  const [readAt, setReadAt] = React.useState('');
  const [clientKey, setClientKey] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState(false);
  const readings = usePortalTankReadings(tank.tankId, open);
  const add = useAddPortalTankReading(tank.tankId);

  const openPanel = (): void => {
    setLevel('');
    setReadAt(toLocalInput(new Date()));
    setClientKey(newClientKey());
    setError(null);
    setSaved(false);
    setOpen(true);
  };

  const levelNumber = level.trim() === '' ? NaN : Number(level);
  const setUp = readings.data?.setUp ?? true;

  const submit = (): void => {
    if (!Number.isFinite(levelNumber) || levelNumber < 0) {
      setError(unit === 'BBL' ? 'Enter the level in BBL.' : 'Enter the level in inches.');
      return;
    }
    const when = readAt === '' ? new Date() : new Date(readAt);
    add.mutate(
      {
        ...(unit === 'BBL' ? { levelBbl: levelNumber } : { levelInches: levelNumber }),
        readAt: when.toISOString(),
        clientKey,
      },
      {
        onSuccess: () => {
          setSaved(true);
          setError(null);
          setLevel('');
          setClientKey(newClientKey());
        },
        onError: (err) => setError(portalReadingErrorMessage(err, tank)),
      },
    );
  };

  if (!open) {
    return (
      <button
        type="button"
        style={buttonStyle}
        aria-label={`Enter a reading for ${tank.tankName}`}
        onClick={openPanel}
      >
        Enter reading
      </button>
    );
  }

  return (
    <div
      role="group"
      aria-label={`Reading for ${tank.tankName}`}
      style={{ display: 'grid', gap: '8px', marginTop: '8px' }}
    >
      {!setUp && (
        <p role="status" style={{ margin: 0, fontSize: '13px', color: 'var(--color-warning)' }}>
          {tank.tankName} is not set up yet. Set it up under Tank setup, then enter readings.
        </p>
      )}
      <div role="radiogroup" aria-label="Unit" style={{ display: 'flex', gap: '6px' }}>
        {(
          [
            ['BBL', 'BBL'],
            ['IN', 'Inches'],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={unit === value}
            onClick={(): void => setUnit(value)}
            style={{
              ...buttonStyle,
              minHeight: '28px',
              background: unit === value ? 'var(--color-accent-blue)' : 'var(--color-bg-tertiary)',
              color: unit === value ? 'var(--color-on-accent)' : 'var(--color-text-primary)',
            }}
          >
            {label}
          </button>
        ))}
      </div>
      <label style={{ fontSize: '13px' }}>
        {unit === 'BBL' ? 'Level (BBL)' : 'Level (inches)'}
        <input
          type="number"
          inputMode="decimal"
          min={0}
          step="0.01"
          value={level}
          aria-label={unit === 'BBL' ? 'Level (BBL)' : 'Level (inches)'}
          onChange={(e): void => setLevel(e.target.value)}
          style={inputStyle}
        />
      </label>
      <label style={{ fontSize: '13px' }}>
        Read at
        <input
          type="datetime-local"
          value={readAt}
          aria-label="Read at"
          onChange={(e): void => setReadAt(e.target.value)}
          style={inputStyle}
        />
      </label>
      {error !== null && (
        <p role="alert" style={{ margin: 0, color: 'var(--color-danger)', fontSize: '13px' }}>
          {error}
        </p>
      )}
      {saved && error === null && (
        <p role="status" style={{ margin: 0, color: 'var(--color-success)', fontSize: '13px' }}>
          Reading saved.
        </p>
      )}
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          type="button"
          style={{
            ...buttonStyle,
            background: 'var(--color-accent-blue)',
            border: '1px solid var(--color-accent-blue)',
            color: 'var(--color-on-accent)',
          }}
          disabled={add.isPending || !setUp}
          onClick={submit}
        >
          {add.isPending ? 'Saving…' : 'Save reading'}
        </button>
        <button type="button" style={buttonStyle} onClick={(): void => setOpen(false)}>
          Close
        </button>
      </div>
      {(readings.data?.readings.length ?? 0) > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, fontSize: '13px' }}>
          {(readings.data?.readings ?? []).slice(0, 5).map((r) => (
            <ReadingItem key={r.id} tankId={tank.tankId} reading={r} />
          ))}
        </ul>
      )}
    </div>
  );
}
